import React, { useMemo, useState } from "react";
import { Segmented, Space } from "antd";
import { useTranslation } from "react-i18next";

import { MarketingLeadStatus, type MarketingLead } from "@fitness-app/data-models/entities/MarketingLead";

import { formatPercent, formatPln, toFiniteNumber } from "../marketingFormat";
import { LeadsTable } from "./LeadsTable";

interface Props {
  leads: MarketingLead[];
}

type LeadsFilter = "all" | "showed" | "sale";

const filterLeads = (leads: MarketingLead[], filter: LeadsFilter): MarketingLead[] => {
  switch (filter) {
    case "showed":
      return leads.filter((lead) => lead.status === MarketingLeadStatus.Showed);
    case "sale":
      return leads.filter((lead) => lead.sale);
    default:
      return leads;
  }
};

/** Expanded campaign row: its meetings with a quick outcome summary. */
export const CampaignLeadsPanel: React.FC<Props> = ({ leads }) => {
  const { t } = useTranslation("dashboard");
  const [filter, setFilter] = useState<LeadsFilter>("all");

  const summary = useMemo(() => {
    const now = Date.now();
    const past = leads.filter((lead) => new Date(lead.startTime).getTime() <= now);
    const showed = leads.filter((lead) => lead.status === MarketingLeadStatus.Showed);
    const sales = leads.filter((lead) => lead.sale);
    const contractedValue = sales.reduce((sum, lead) => sum + (toFiniteNumber(lead.contractValue) ?? 0), 0);

    return {
      meetings: leads.length,
      showed: showed.length,
      sales: sales.length,
      contractedValue,
      srPercent: past.length ? (showed.length / past.length) * 100 : null,
      closeRatePercent: showed.length ? (sales.length / showed.length) * 100 : null,
    };
  }, [leads]);

  const visibleLeads = useMemo(() => filterLeads(leads, filter), [leads, filter]);

  const summaryItems: Array<{ key: string; value: React.ReactNode }> = [
    { key: "meetings", value: summary.meetings },
    { key: "showed", value: summary.showed },
    { key: "showRate", value: formatPercent(summary.srPercent) },
    { key: "sales", value: summary.sales },
    { key: "closeRate", value: formatPercent(summary.closeRatePercent) },
    { key: "contractedValue", value: formatPln(summary.contractedValue) },
  ];

  return (
    <div className="flex flex-col gap-3 py-2">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
        {summaryItems.map((item) => (
          <span key={item.key} className="text-sm">
            <span className="text-gray-500">{t(`marketingAnalytics.campaignLeads.summary.${item.key}`)}: </span>
            <span className="font-semibold tabular-nums">{item.value}</span>
          </span>
        ))}
      </div>
      <Space>
        <Segmented<LeadsFilter>
          options={[
            { label: t<string>("marketingAnalytics.campaignLeads.filters.all"), value: "all" },
            { label: t<string>("marketingAnalytics.campaignLeads.filters.showed"), value: "showed" },
            { label: t<string>("marketingAnalytics.campaignLeads.filters.sale"), value: "sale" },
          ]}
          value={filter}
          onChange={(value) => setFilter(value)}
        />
      </Space>
      <LeadsTable leads={visibleLeads} loading={false} hideCampaignColumn />
    </div>
  );
};
